/**
 * ERP Drills Tab Component
 * Emergency drill scheduling and management
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Calendar, Eye, Edit2, Trash2 } from 'lucide-react';
import { EMERGENCY_TYPES } from './ERPDashboardTab';

export default function ERPDrillsTab({ 
  drills,
  onShowDrillDialog,
  onViewDrill,
  onEditDrill,
  onDeleteDrill
}) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={onShowDrillDialog}>
          <Plus className="w-4 h-4 mr-2" /> Schedule Drill
        </Button>
      </div>
      
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="text-left p-4 text-slate-400">Drill</th>
                <th className="text-left p-4 text-slate-400">Type</th>
                <th className="text-left p-4 text-slate-400">Date</th>
                <th className="text-left p-4 text-slate-400">Status</th>
                <th className="text-left p-4 text-slate-400">Score</th>
                <th className="text-left p-4 text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {drills.map(drill => {
                const config = EMERGENCY_TYPES[drill.emergency_type] || EMERGENCY_TYPES.other;
                return (
                  <tr key={drill.id} className="border-t border-slate-700">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <config.icon className={`w-5 h-5 text-${config.color}-400`} />
                        <div>
                          <p className="text-white font-medium">{drill.name}</p>
                          <p className="text-xs text-slate-500">{drill.location}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className="capitalize">{drill.drill_type}</Badge>
                    </td>
                    <td className="p-4 text-slate-400">{drill.scheduled_date}</td>
                    <td className="p-4">
                      <Badge className={
                        drill.status === 'completed' ? 'bg-emerald-500/20 text-emerald-300' :
                        drill.status === 'scheduled' ? 'bg-blue-500/20 text-blue-300' :
                        'bg-slate-500/20 text-slate-300'
                      }>
                        {drill.status}
                      </Badge>
                    </td>
                    <td className="p-4 text-slate-400">{drill.score || '-'}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => onViewDrill(drill)} title="View">
                          <Eye className="w-4 h-4 text-slate-400" />
                        </Button>
                        {drill.status !== 'completed' && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => onEditDrill(drill)} title="Edit">
                              <Edit2 className="w-4 h-4 text-blue-400" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => onDeleteDrill(drill.id)} title="Delete">
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {drills.length === 0 && (
            <div className="p-8 text-center">
              <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No drills scheduled</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
